import React from 'react'
import { useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import BankAccountOption from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/BankAccountOption'
import { useGetBankAccountInfo } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/GetBankAccountInfo.gql'
import { useSelectConnection } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/SelectConnection.gql'
import { isBankInfoPopulated } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/utils'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import { useRemoveManualBankAccount } from './RemoveManualBankAccount.gql'
import styles from './styles.module.scss'

const ManualBankAccount = () => {
  const [getClassName, makeTestId] = useNameFormatter('ManualBankAccount')
  const { appId } = useParams()
  const showToast = useToast()
  const { tracker } = useWebTracking()

  const [removeManualBankAccount, { loading: isRemoveManualBankAccountLoading }] = useRemoveManualBankAccount({
    variables: {
      creditApplicationId: appId ?? '',
      financeInfo: {
        bankInfo: {
          accountHolderName: '',
          accountNumber: '',
          bankName: '',
          routingNumber: '',
        },
      },
    },
    onCompleted: () => {
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Bank account removed'
          id='sputnik.ManualBankAccount__YR2TGa'
        />,
        { autoHideDelay: 3000 },
      )
    },
    onError: () => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to remove bank account'
          id='sputnik.ManualBankAccount__LaUH90'
        />,
        { className: 'fs-unmask' },
      )
    },
  })
  const [selectConnection, { loading: isSelectConnectionLoading }] = useSelectConnection()

  const { data } = useGetBankAccountInfo({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const onSelectBankConnection = async () => {
    await selectConnection({
      variables: {
        creditApplicationId: appId ?? '',
        financeInfo: {
          selectedExternalId: null,
        },
      },
      onCompleted: () => {
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Successfully selected {name} as primary'
            id='sputnik.ManualBankAccount__Ozt1cl'
            values={{ name: bankName }}
          />,
          { autoHideDelay: 3000 },
        )
      },
      onError: () => {
        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='Failed to select <span>{bankName}</span> as primary'
            id='sputnik.ManualBankAccount__Q6WF9Y'
            values={{
              span: (chunks) => <span className='fs-mask'>{chunks}</span>,
              bankName,
            }}
          />,
          { className: 'fs-unmask' },
        )
      },
    })
  }

  const manualBankAccount = data?.creditApplication?.financeInfo?.bankInfo
  const selectedBankAccount = data?.creditApplication?.financeInfo?.selectedBankInfo

  if (!manualBankAccount || !isBankInfoPopulated(manualBankAccount)) {
    return null
  }

  const { bankName, id: manualBankAccountId, accountNumberLastFour } = manualBankAccount
  const { id: selectedBankAccountId } = selectedBankAccount ?? {}

  return (
    <div
      className={styles[getClassName('')]}
      data-testid={makeTestId('')}
    >
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Accounts added manually'
          id='sputnik.ManualBankAccount__VujF20'
        />
      </div>
      <BankAccountOption
        isSelected={manualBankAccountId === selectedBankAccountId}
        isStateChanging={isSelectConnectionLoading || isRemoveManualBankAccountLoading}
        lastFour={accountNumberLastFour ?? ''}
        name={bankName ?? ''}
        onDelete={() => {
          tracker?.track('se_signup__application_financial_info_connect_manual_delete_bank__fired')
          removeManualBankAccount()
        }}
        onSelected={onSelectBankConnection}
      />
    </div>
  )
}

export default ManualBankAccount
